import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/Section";

// Approved content — the document's reordered 10-point buyer guide, verbatim.
const ITEMS = [
  {
    q: "Is the service customized to meet your needs?",
    a: "A cleaning company should ask about your requirements, document the scope, and clearly define what will be done, when, and how often.",
  },
  {
    q: "Is there ongoing follow-up every week?",
    a: "As your needs change. Regular customer feedback helps keep service aligned and allows adjustments to be made quickly.",
  },
  {
    q: "Is a manager available day or night to resolve problems immediately?",
    a: "A service problem should never linger unresolved. Responsive management reduces disruption.",
  },
  {
    q: "Is the company in business for the long term?",
    a: "Look for stability, repeat customers, operating history, and systems designed to protect service continuity.",
  },
  {
    q: "Is the same work team assigned to your site week after week?",
    a: "Consistency helps employees learn the facility, its priorities, and the details that matter to the customer.",
  },
  {
    q: "How carefully are employees screened and hired?",
    a: "Ask about dependability, references, work ethic, attendance expectations, and performance evaluation.",
  },
  {
    q: "Are employees trained for your specific site and needs?",
    a: "Site-specific instruction and written procedures reduce inconsistency and guesswork.",
  },
  {
    q: "Will management care after you sign the contract?",
    a: "Management should remain accessible after the sale and continue to evaluate customer satisfaction.",
  },
  {
    q: "Is the company bonded and insured?",
    a: "Commercial cleaning brings workers into your facility. Appropriate coverage is an important risk-management requirement.",
  },
  {
    q: "Is there a clear satisfaction and problem-resolution commitment?",
    a: "Know how concerns are reported, how quickly the company responds, and who is accountable for resolving them.",
  },
];

const pad = (n) => String(n + 1).padStart(2, "0");

export default function FAQ() {
  return (
    <>
      <Seo
        title="Quality, Satisfaction & Buyer Guide"
        description="10 things to look for when choosing a commercial cleaning company — customization, follow-up, responsive management, stability, consistent teams, screening, training, accountability, insurance and satisfaction."
        path="/faq"
      />
      <PageHero
        eyebrow="Quality, Satisfaction & Buyer Guide"
        title={["10 things to look for", "when choosing a", "commercial cleaner."]}
      />

      {/* Desktop — editorial expanded layout */}
      <section className="container-px mx-auto hidden max-w-[1500px] py-20 lg:block lg:py-28">
        <div className="border-t border-border">
          {ITEMS.map((item, i) => (
            <Reveal key={i}>
              <div
                data-testid={`buyer-item-${i}`}
                className="group grid grid-cols-[auto_1fr_1.2fr] items-start gap-12 border-b border-border py-10"
              >
                <span className="font-display text-5xl font-black leading-none text-navy/12 transition-colors duration-300 group-hover:text-brand-green">
                  {pad(i)}
                </span>
                <h2 className="font-display text-xl font-bold leading-snug tracking-tight text-navy xl:text-2xl">
                  {item.q}
                </h2>
                <p className="text-base leading-relaxed text-steel xl:text-lg">{item.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Mobile — accordion */}
      <section className="container-px mx-auto max-w-[900px] py-14 lg:hidden">
        <Accordion type="single" collapsible className="border-t border-border" data-testid="buyer-accordion">
          {ITEMS.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
              <AccordionTrigger
                data-testid={`buyer-trigger-${i}`}
                className="gap-4 py-5 text-left font-display text-base font-semibold tracking-tight text-navy hover:no-underline"
              >
                <span className="flex items-start gap-3">
                  <span className="font-display text-sm font-black text-brand-green-ink">{pad(i)}</span>
                  {item.q}
                </span>
              </AccordionTrigger>
              <AccordionContent data-testid={`buyer-content-${i}`} className="pb-5 pl-8 text-base leading-relaxed text-steel">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <CtaBand
        title="Request a Customized Cleaning Plan"
        body=""
        primary={{ label: "Request a Customized Cleaning Plan", to: "/request-a-quote" }}
        secondary={{ label: "Talk With Our Team", to: "/contact" }}
      />
    </>
  );
}
